// src/pages/Terms.jsx
import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

/* Renders prose with any email address converted to a mailto link.
   The split pattern is global so it can capture every occurrence; the test
   pattern is deliberately NOT global, because calling .test() on a /g regex
   mutates lastIndex and returns alternating results. */
const EMAIL_SPLIT = /([\w.+-]+@[\w-]+\.[\w.-]+)/g
const EMAIL_EXACT = /^[\w.+-]+@[\w-]+\.[\w.-]+$/

function linkifyEmails(text) {
    return text.split(EMAIL_SPLIT).map((part, i) =>
        EMAIL_EXACT.test(part) ? (
            <a key={i} href={`mailto:${part}`} className="mail-link">
                {part}
            </a>
        ) : (
            part
        )
    )
}

const LAST_UPDATED = 'June 2026'

const SECTIONS = [
    {
        id: '01',
        title: 'About Guardian Message Check',
        body: `Guardian Message Check ("GMC") is an AI-powered communication analysis platform that uses advanced artificial intelligence technologies to analyse written communications, generate communication insights, provide communication risk assessments and produce professional rewrite suggestions.

The platform is designed to assist individuals, professionals and organisations in understanding written communications by providing:
• Communication risk assessments
• Plain-English explanations
• Suggested professional rewrites
• Communication insights
• Decision-support information

Guardian Message Check analyses communications using a structured multi-stage assessment process which may include risk scoring, contextual analysis and optional rewrite generation.

The platform is designed to assist users in making better-informed communication decisions but does not replace professional judgement.`,
    },
    {
        id: '02',
        title: 'Eligibility',
        body: `By creating an account or using Guardian Message Check you confirm that:
• you are at least 18 years of age;
• you have the legal capacity to enter into a legally binding agreement;
• all information you provide is accurate and kept up to date;
• you will use the platform only for lawful purposes;
• if using the platform on behalf of a business or organisation, you have authority to bind that organisation to these Terms.

Guardian Message Check reserves the right to suspend or terminate accounts where false information has been provided.`,
    },
    {
        id: '03',
        title: 'User Accounts',
        body: `You are responsible for maintaining the confidentiality of your account credentials.

You agree to:
• keep your password secure;
• not share your account with another person unless your subscription specifically permits multi-user access;
• notify Guardian Message Check immediately if you believe your account has been compromised;
• keep your email address and account details current.

You are responsible for all activity carried out under your account unless unauthorised access resulted from a security failure attributable to Guardian Message Check.

We reserve the right to suspend or restrict access where we reasonably believe an account has been compromised or is being misused.`,
    },
    {
        id: '04',
        title: 'Subscription Plans and Pay-As-You-Go',
        body: `Guardian Message Check currently offers several methods of accessing the platform, including:
• Free Accounts
• Individual Subscription Plans
• Pay-As-You-Go (PAYG)
• Business Plans
• Enterprise Plans

Current pricing and available features are displayed on the Pricing page of the website.

Guardian Message Check reserves the right to introduce, modify or discontinue plans from time to time.

Where pricing changes affect an existing subscription, subscribers will receive reasonable advance notice before the new pricing takes effect at the next renewal.

Enterprise customers may operate under separate commercial agreements where applicable.`,
    },
    {
        id: '05',
        title: 'Payments and Cancellations',
        body: `Payments are processed securely through approved third-party payment providers.

Subscription Plans

Subscriptions renew automatically at the end of each billing cycle unless cancelled before the renewal date.

Users may cancel their subscription at any time through their account settings.

Cancellation prevents future billing but does not entitle the user to a refund for any unused portion of the current subscription period.

Access continues until the end of the paid billing period.

Pay-As-You-Go

Where available, Pay-As-You-Go purchases are one-time purchases.

Except where required by applicable law, PAYG purchases are non-refundable.

Unused credits cannot be exchanged for cash.

Guardian Message Check reserves the right to amend PAYG pricing in the future.`,
    },
    {
        id: '06',
        title: 'Free Usage',
        body: `Guardian Message Check may provide a Free plan which includes a limited number of analyses.

Free usage allowances, limits and available features may change from time to time.

Guardian Message Check reserves the right to modify, suspend or withdraw free plan features where reasonably necessary for operational, commercial or security reasons.

Use of the Free plan does not create any entitlement to continued access to particular features or usage allowances.`,
    },
    {
        id: '07',
        title: 'AI Analysis and Limitations',
        body: `Guardian Message Check utilises advanced artificial intelligence technologies to analyse written communications and provide structured communication insights.

The platform generally follows a two-stage assessment process consisting of:

Stage One
Communication risk assessment, including classification into one of the platform's communication risk tiers.

Stage Two
A detailed narrative explanation together with suggested professional rewrite options where applicable.

The analysis, explanations, recommendations and rewritten communications generated by Guardian Message Check are provided for informational and educational purposes only.

While Guardian Message Check has been designed to provide consistent, balanced and high-quality communication analysis, artificial intelligence systems may occasionally generate inaccurate, incomplete, unexpected or inappropriate outputs.

Users should always exercise their own independent judgement before relying upon any analysis, recommendations or rewritten communications produced by the platform.

Where appropriate, users should obtain advice from a suitably qualified legal, financial, employment or other professional adviser before making important decisions.

Users remain solely responsible for every decision they make and every communication they choose to send.

Guardian Message Check accepts no responsibility for any decision, action or omission made by a user based upon platform output.`,
    },
    {
        id: '08',
        title: 'AI Rewrite Service',
        body: `Where available, Guardian Message Check may generate suggested rewritten communications based upon content submitted by the user.

These rewrites are intended solely as communication assistance.

Users are solely responsible for reviewing, editing, approving and deciding whether to use any rewritten communication before sending, publishing or otherwise relying upon it.

Guardian Message Check does not guarantee that any rewritten communication will achieve a desired outcome, avoid legal disputes, prevent conflict or accurately reflect the user's intentions.

Responsibility for the final communication always remains with the user.`,
    },
    {
        id: '09',
        title: 'No Professional Advice',
        body: `Guardian Message Check is a technology platform.

It is not a law firm.
It is not a solicitor.
It is not an accountant.
It is not an HR consultancy.
It is not a financial adviser.

Nothing produced by Guardian Message Check constitutes legal advice, financial advice, employment advice, tax advice or any other regulated professional advice.

The platform provides AI-assisted communication analysis only.

Where professional advice is required, users should consult an appropriately qualified professional.

No solicitor-client, consultant-client or other professional relationship is created through use of Guardian Message Check.`,
    },
    {
        id: '10',
        title: 'Enterprise and Business Customers',
        body: `Guardian Message Check offers Business and Enterprise solutions designed for organisations requiring multi-user access, administration tools and enhanced functionality.

Enterprise customers may also enter into separate commercial agreements, including Master Services Agreements (MSAs), Service Level Agreements (SLAs) or other contractual arrangements.

Where a separately negotiated written agreement conflicts with these Terms, the separate written agreement shall prevail to the extent of that conflict.

Business administrators remain responsible for ensuring that authorised users within their organisation comply with these Terms.

Enterprise API customers may also be subject to separate API usage terms where applicable.`,
    },
    {
        id: '11',
        title: 'User Content and Intellectual Property',
        body: `Your Content

Users retain ownership of all communications, documents, emails, letters, contracts, messages and other content submitted to Guardian Message Check.

Users also retain ownership of any rewritten communications generated from their own submitted content.

By submitting content, users confirm that they have the legal right and authority to submit that content for analysis.

Users remain solely responsible for the legality and accuracy of any material submitted to the platform.

Our Intellectual Property

Guardian Message Check, including its software, source code, branding, logos, designs, user interface, databases, algorithms, documentation and platform functionality, remains the exclusive intellectual property of Guardian Message Check or its licensors.

Nothing within these Terms transfers ownership of the platform or its intellectual property to any user.

Users may not copy, reproduce, modify, distribute, reverse engineer or commercially exploit any part of the platform except where expressly permitted by law or by written permission.`,
    },
    {
        id: '12',
        title: 'Zero Retention and Data Processing',
        body: `Guardian Message Check operates using a Zero Retention framework across all plans offered on the platform, including Free, Pay-As-You-Go, Subscription, Business and Enterprise accounts.

Message content submitted for analysis is processed solely for the purpose of generating the requested communication analysis and, where applicable, rewrite suggestions.

Guardian Message Check does not intentionally retain, permanently store or build databases containing the body text of submitted communications after processing has completed.

Temporary processing necessary for platform operation, fraud prevention, cybersecurity, service integrity, debugging or compliance with applicable law may occur where reasonably required.

Guardian Message Check does not use submitted communications to train proprietary artificial intelligence models.

Further information regarding the processing of personal information is contained within the Guardian Message Check Privacy Policy.`,
    },
    {
        id: '13',
        title: 'Privacy and Data Protection',
        body: `Guardian Message Check is committed to protecting the privacy and personal information of its users.

Personal data is processed in accordance with applicable data protection legislation, including the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.

The collection, processing, storage and protection of personal information are explained in greater detail within the Guardian Message Check Privacy Policy.

Users are encouraged to read the Privacy Policy before using the platform.

Where required by law, Guardian Message Check will cooperate with lawful requests from regulatory authorities or law enforcement agencies.`,
    },
    {
        id: '14',
        title: 'Acceptable Use Policy',
        body: `Users agree to use Guardian Message Check responsibly, lawfully and in accordance with these Terms.

Users must not:
• use the platform for any unlawful purpose;
• submit communications which they do not have the legal right to analyse;
• attempt to gain unauthorised access to any part of the platform;
• interfere with or disrupt the operation of the service;
• reverse engineer, decompile or otherwise attempt to discover the underlying source code or technology of the platform except where expressly permitted by applicable law;
• scrape, harvest or automate access to the platform without written permission;
• introduce malicious software, viruses, ransomware or harmful code;
• attempt to bypass usage limits, subscription controls or security measures;
• use the platform to facilitate fraud, harassment, unlawful discrimination, criminal activity or any other illegal conduct;
• resell, lease, sublicense or commercially exploit the platform without written authorisation.

Guardian Message Check reserves the right to suspend or permanently terminate accounts that breach this Acceptable Use Policy.

Serious breaches may also be reported to the appropriate authorities where required by law.`,
    },
    {
        id: '15',
        title: 'Platform Availability and Maintenance',
        body: `Guardian Message Check aims to provide a reliable, secure and continuously improving platform.

However, uninterrupted availability cannot be guaranteed.

The platform may occasionally become unavailable due to:
• planned maintenance;
• software updates;
• emergency security work;
• infrastructure failures;
• third-party provider outages;
• circumstances beyond our reasonable control.

Where reasonably practicable, advance notice of scheduled maintenance will be provided.

Guardian Message Check accepts no liability for temporary interruptions that are reasonably necessary to maintain, improve or secure the platform.`,
    },
    {
        id: '16',
        title: 'Limitation of Liability',
        body: `To the fullest extent permitted by applicable law, Guardian Message Check shall not be liable for any indirect, incidental, consequential, special or economic losses arising out of or connected with the use of the platform.

This includes, but is not limited to:
• loss of profits;
• loss of revenue;
• loss of business opportunities;
• loss of contracts;
• loss of goodwill;
• loss of reputation;
• loss of anticipated savings;
• business interruption;
• loss or corruption of data.

Guardian Message Check's total aggregate liability for any claim arising under or in connection with these Terms shall not exceed the total amount paid by the user to Guardian Message Check during the twelve (12) months immediately preceding the event giving rise to the claim.

Nothing within these Terms excludes or limits liability where such exclusion is prohibited by law, including liability for:
• death or personal injury caused by negligence;
• fraud or fraudulent misrepresentation;
• any other liability which cannot lawfully be excluded.

Users acknowledge that Guardian Message Check provides AI-assisted communication analysis and that ultimate responsibility for decisions, communications and actions always remains with the user.`,
    },
    {
        id: '17',
        title: 'Complaints and Dispute Resolution',
        body: `Guardian Message Check values customer feedback and aims to resolve concerns fairly and efficiently.

If a user wishes to make a complaint, they should first contact:

Email: admin@guardianmessagecheck.com

Guardian Message Check aims to:
• acknowledge complaints within five (5) working days;
• investigate complaints fairly;
• provide a substantive response as soon as reasonably practicable;
• where possible, resolve complaints within twenty-eight (28) days.

Both parties agree to make reasonable efforts to resolve disputes through good-faith discussions before commencing formal legal proceedings.

Nothing within this clause prevents either party from seeking urgent legal remedies where necessary.`,
    },
    {
        id: '18',
        title: 'Termination',
        body: `Guardian Message Check reserves the right to suspend, restrict or terminate any user account where it reasonably believes that:
• these Terms and Conditions have been breached;
• the platform has been used unlawfully;
• fraudulent activity has occurred;
• platform security has been compromised;
• payment obligations have not been met;
• continued access presents a security, legal or operational risk.

Where reasonably practicable, Guardian Message Check will notify users before permanent termination.

Users may close their account at any time through their account settings.

Termination of an account does not entitle the user to a refund for unused subscription time or unused Pay-As-You-Go purchases except where required by applicable law.

The following provisions shall survive termination where applicable:
• Intellectual Property
• Limitation of Liability
• Governing Law
• Dispute Resolution
• Any provisions intended to survive termination.`,
    },
    {
        id: '19',
        title: 'Amendments to These Terms and the Platform',
        body: `Guardian Message Check may amend these Terms from time to time to reflect changes in legislation, technology, business operations or platform functionality.

Where material changes are made, registered users will receive reasonable advance notice by email, platform notification or another appropriate method.

Continued use of Guardian Message Check after amended Terms become effective constitutes acceptance of those updated Terms.

Guardian Message Check also reserves the right to improve, modify, replace or discontinue features of the platform where reasonably necessary.

Where a material feature forming part of a paid subscription is permanently withdrawn, Guardian Message Check will endeavour to provide reasonable notice and, where appropriate, a suitable alternative, credit or refund in accordance with applicable consumer law.`,
    },
    {
        id: '20',
        title: 'Governing Law and Jurisdiction',
        body: `These Terms and Conditions shall be governed by and interpreted in accordance with the laws of England and Wales.

Any dispute arising out of or relating to these Terms, the Guardian Message Check platform or its services shall be subject to the exclusive jurisdiction of the courts of England and Wales.

Nothing within these Terms limits any statutory consumer rights which cannot lawfully be excluded.`,
    },
    {
        id: '21',
        title: 'Contact Information',
        body: `For any questions regarding these Terms and Conditions, the Guardian Message Check platform or your account, please contact:

Guardian Message Check

Email: admin@guardianmessagecheck.com

Website: guardianmessagecheck.com

Support enquiries should be submitted using the Contact page available through the Guardian Message Check website wherever possible.

Entire Agreement

These Terms and Conditions constitute the entire agreement between Guardian Message Check and the user relating to the use of the platform and supersede any previous discussions, representations or agreements relating to the services unless otherwise agreed in writing.

If any provision of these Terms is found to be unlawful, invalid or unenforceable by a court of competent jurisdiction, the remaining provisions shall remain in full force and effect.`,
    },
]

export default function Terms() {
    const [activeSection, setActiveSection] = useState('01')
    const observerRefs = useRef({})

    // Active Table of Contents highlighting based on current user scroll view
    useEffect(() => {
        const handleIntersection = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id.replace('section-', ''))
                }
            })
        }

        const observer = new IntersectionObserver(handleIntersection, {
            root: null,
            rootMargin: '-20% 0px -60% 0px',
            threshold: 0
        })

        SECTIONS.forEach((s) => {
            const el = document.getElementById(`section-${s.id}`)
            if (el) observer.observe(el)
        })

        return () => observer.disconnect()
    }, [])

    const handleTOCClick = (e, id) => {
        e.preventDefault()
        const element = document.getElementById(`section-${id}`)
        if (element) {
            const offset = 130
            const bodyRect = document.body.getBoundingClientRect().top
            const elementRect = element.getBoundingClientRect().top
            const elementPosition = elementRect - bodyRect
            const offsetPosition = elementPosition - offset

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            })
            setActiveSection(id)
        }
    }

    return (
        <div className="min-h-screen flex flex-col bg-[#07090e] text-slate-300 antialiased relative overflow-x-hidden">

            {/* Top Structural Decorative Mesh Glows */}
            <div className="absolute top-0 right-1/4 w-[500px] h-[250px] bg-indigo-500/[0.02] blur-[100px] pointer-events-none rounded-full" />
            <div className="absolute top-[20%] left-0 w-[400px] h-[400px] bg-brand-amber/[0.01] blur-[120px] pointer-events-none rounded-full" />

            <Navbar />

            <div className="flex-1 max-w-6xl mx-auto w-full px-6 py-16 lg:py-24 relative z-10">

                {/* -- Header Block ------------------------------------------ */}
                <div className="mb-16 border-b border-white/[0.05] pb-12">
                    <div className="inline-flex items-center gap-2.5 bg-brand-amber/[0.06] border border-brand-amber/25 rounded-full px-5 py-2 mb-6 select-none">
                        <span className="w-2 h-2 rounded-full bg-brand-amber animate-pulse shadow-[0_0_6px_rgba(212,168,67,0.9)]" />
                        <span className="text-sm font-sans font-semibold tracking-widest text-brand-amber uppercase">
                            Terms of Service
                        </span>
                    </div>

                    <h1 className="font-sans font-bold text-3xl sm:text-4xl text-white tracking-tight mb-4">
                        Terms and Conditions
                    </h1>
                    <p className="text-base font-sans font-light text-slate-400 max-w-2xl leading-[1.8] mb-7">
                        These Terms and Conditions govern your access to and use of the Guardian Message Check platform, website and associated services.
                    </p>

                    <div className="flex flex-wrap items-center gap-y-2 gap-x-5 text-sm font-sans font-light text-slate-400">
                        <span className="flex items-center gap-1.5">
                            <span className="text-slate-600">Last updated:</span>
                            <span className="text-slate-400 font-medium">June 2026</span>
                        </span>
                        <span className="text-white/[0.10] hidden sm:inline">|</span>
                        <span className="flex items-center gap-1.5">
                            <span className="text-slate-600">Jurisdiction:</span>
                            <span className="text-slate-400 font-medium">England & Wales</span>
                        </span>
                        <span className="text-white/[0.10] hidden sm:inline">|</span>
                        <span className="text-slate-500 font-medium tracking-wide">Guardian Message Check</span>
                    </div>
                </div>

                {/* -- Asymmetric Document Layout ---------------------------- */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 items-start">

                    {/* Left Sticky Content Navigation Router */}
                    <nav className="lg:sticky lg:top-28 hidden lg:block select-none max-h-[70vh] overflow-y-auto pr-2 scrollbar-thin">
                        <p className="text-xs font-sans font-bold tracking-widest uppercase text-slate-400 mb-6">
                            Document Matrix
                        </p>
                        <ol className="space-y-3">
                            {SECTIONS.map(s => {
                                const isCurrent = activeSection === s.id
                                return (
                                    <li key={s.id}>
                                        <a
                                            href={`#section-${s.id}`}
                                            onClick={(e) => handleTOCClick(e, s.id)}
                                            className="flex items-start gap-3 group text-sm transition-colors duration-200 py-1"
                                        >
                                            <span className={`font-sans text-xs tracking-wide mt-0.5 flex-shrink-0 transition-colors duration-200 ${isCurrent ? 'text-brand-amber font-semibold' : 'text-slate-600 group-hover:text-slate-400'
                                                }`}>
                                                {s.id}
                                            </span>
                                            <span className={`font-sans font-light leading-snug transition-colors duration-200 ${isCurrent ? 'text-white font-medium' : 'text-slate-400 group-hover:text-slate-200'
                                                }`}>
                                                {s.title}
                                            </span>
                                        </a>
                                    </li>
                                )
                            })}
                        </ol>
                    </nav>

                    {/* Right Core Content Delivery Track */}
                    <div className="lg:col-span-3 space-y-0">
                        {SECTIONS.map((s, i) => (
                            <div
                                key={s.id}
                                id={`section-${s.id}`}
                                className={`py-16 first:pt-0 ${i < SECTIONS.length - 1 ? 'border-b border-white/[0.04]' : ''
                                    }`}
                            >
                                <div className="flex items-start gap-4 mb-6 select-none">
                                    <span className="font-sans text-sm font-semibold text-slate-500 mt-1 flex-shrink-0 bg-white/[0.02] border border-white/[0.05] rounded-md w-8 h-8 flex items-center justify-center">
                                        {s.id}
                                    </span>
                                    <h2 className="font-sans font-semibold text-2xl text-white tracking-tight leading-snug">
                                        {s.title}
                                    </h2>
                                </div>
                                <div className="pl-0 sm:pl-10 space-y-6">
                                    {s.body.split('\n\n').map((para, j) => (
                                        <p key={j} className="text-base font-sans font-light text-slate-400 leading-[1.85] whitespace-pre-line">
                                            {linkifyEmails(para)}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Interactive Legal Inquiry Card Footer */}
                        <div className="mt-14 rounded-2xl bg-white/[0.01] border border-white/[0.05] p-6 sm:p-8 shadow-2xl backdrop-blur-md relative overflow-hidden group">
                            <div className="absolute inset-0 pointer-events-none"
                                style={{ background: 'radial-gradient(circle at top right, rgba(212,168,67,0.02) 0%, transparent 50%)' }} />

                            <div className="relative z-10">
                                <p className="text-xs font-sans font-bold tracking-widest uppercase text-slate-400 mb-2.5">
                                    Legal Operations Desk
                                </p>
                                <h3 className="text-lg font-sans font-semibold text-white mb-2">
                                    Have structural validation questions?
                                </h3>
                                <p className="text-base font-sans font-light text-slate-400 leading-relaxed mb-6 max-w-2xl">
                                    For clarifying details regarding these Terms and Conditions, contact our team directly.
                                </p>
                                <a
                                    href="mailto:admin@guardianmessagecheck.com"
                                    className="inline-flex items-center gap-1.5 text-xs font-sans font-medium text-brand-amber hover:text-brand-amber2 transition-colors"
                                >
                                    <span>admin@guardianmessagecheck.com</span>
                                    <span className="transform group-hover:translate-x-0.5 transition-transform duration-200">→</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    )
}